// src/pages/Dashboard.js
import React, { useEffect, useState, useCallback } from "react";
import api from "../api";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Modal from "react-modal";
import "./Dashboard.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

Modal.setAppElement("#root");

const CATEGORY_OPTIONS = [
  { value: "job", label: "Job" },
  { value: "house", label: "House" },
  { value: "scholarship", label: "School/Scholarship" },
];

const CATEGORY_FIELDS = {
  job: [
    { name: "title", label: "Job Title", type: "text", required: true },
    { name: "location", label: "Location", type: "text", required: false },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "salary", label: "Salary", type: "text", required: false },
    { name: "deadline", label: "Deadline", type: "date", required: false },
  ],
  house: [
    { name: "title", label: "House Title", type: "text", required: true },
    { name: "location", label: "Location", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "price", label: "Price", type: "text", required: false },
    { name: "deadline", label: "Deadline", type: "date", required: false },
  ],
  scholarship: [
    { name: "title", label: "Scholarship Title", type: "text", required: true },
    { name: "school", label: "School/University", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "deadline", label: "Deadline", type: "date", required: false },
  ],
};

// ── Fixed welfare payment form ──────────────────────────────
function WelfarePaymentForm({ memberId, amount, onPaymentSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const CARD_ELEMENT_OPTIONS = {
    hidePostalCode: true,
    style: {
      base:    { fontSize: "16px", color: "#333" },
      invalid: { color: "#fa755a" },
    },
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);

    try {
      // Step 1 — create PaymentIntent (amount in dollars, NOT cents)
      const paymentResp = await api.post("members/create-payment-intent/", {
        amount      : amount,
        member_id   : memberId,
        payment_type: "welfare",
      });

      const { client_secret, payment_intent_id } = paymentResp.data;

      // Step 2 — confirm card with Stripe
      const { error } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (error) {
        alert(error.message);
        return;
      }

      // Step 3 — confirm on backend, updates welfare totals
      await api.post("members/confirm-payment/", {
        payment_intent_id,
        member_id   : memberId,
        amount      : amount,
        payment_type: "welfare",
      });

      alert("✅ Welfare payment successful!");
      onPaymentSuccess();

    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Payment failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePayment} className="payment-form">
      <p>💰 Amount to Pay: <strong>${amount.toFixed(2)}</strong></p>
      <div className="card-element-wrapper">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      <button type="submit" disabled={loading}>
        {loading ? "Processing..." : "Pay Now"}
      </button>
    </form>
  );
}

// ── Welfare enrollment form ─────────────────────────────────
function WelfareEnrollmentForm({ welfarePlans, onClose, onSuccess }) {
  const [selectedPlan, setSelectedPlan] = useState("");
  const [paymentPlan,  setPaymentPlan]  = useState("full");
  const [loading,      setLoading]      = useState(false);

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!selectedPlan) { alert("Please select a welfare plan"); return; }
    setLoading(true);
    try {
      await api.post("members/welfare-members/", {
        welfare_plan: selectedPlan,
        payment_plan: paymentPlan,
      });
      alert("✅ Successfully enrolled in welfare program!");
      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to enroll. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="welfare-enrollment-form">
      <h3>Join Welfare Program</h3>
      <form onSubmit={handleEnroll}>
        <div className="form-group">
          <label>Select Welfare Plan</label>
          <select value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value)} required>
            <option value="">Choose a plan...</option>
            {welfarePlans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name} - ${plan.total_amount}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Payment Plan</label>
          <select value={paymentPlan}
            onChange={(e) => setPaymentPlan(e.target.value)} required>
            <option value="full">Pay in Full</option>
            <option value="weekly">Weekly (12 payments)</option>
            <option value="fortnightly">Fortnightly (6 payments)</option>
            <option value="monthly">Monthly (3 payments)</option>
          </select>
        </div>
        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? "Enrolling..." : "Enroll Now"}
          </button>
          <button type="button" onClick={onClose} className="cancel-btn">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Contribution pay form (Stripe) ──────────────────────────
const CARD_OPTS = {
  hidePostalCode: true,
  style: {
    base:    { fontSize: "15px", color: "#333" },
    invalid: { color: "#fa755a" },
  },
};

function ContributionPayForm({ memberId, contribution, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [loading,       setLoading]       = useState(false);
  const [customAmount,  setCustomAmount]  = useState(Number(contribution.amount).toFixed(2));

  const maxAmount = Number(contribution.amount);

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    const payAmt = parseFloat(customAmount);
    if (!payAmt || payAmt <= 0) { alert("Please enter a valid amount."); return; }
    if (payAmt > maxAmount) { alert(`Maximum payable is $${maxAmount.toFixed(2)} AUD.`); return; }
    setLoading(true);
    try {
      const { data: intentData } = await api.post("members/create-payment-intent/", {
        amount      : payAmt,
        member_id   : memberId,
        payment_type: "welfare_contribution",
      });

      const { error } = await stripe.confirmCardPayment(intentData.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (error) { alert(error.message); return; }

      await api.post("members/confirm-payment/", {
        payment_intent_id: intentData.payment_intent_id,
        member_id        : memberId,
        amount           : payAmt,
        payment_type     : "welfare_contribution",
        contribution_id  : contribution.id,
      });

      alert("✅ Payment successful!");
      onSuccess();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePay} className="payment-form" style={{ marginTop: "0.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.6rem" }}>
        <label style={{ fontSize: "0.9rem", fontWeight: 600 }}>💰 Amount (AUD):</label>
        <input
          type="number"
          min="1"
          max={maxAmount}
          step="0.01"
          value={customAmount}
          onChange={(e) => setCustomAmount(e.target.value)}
          style={{
            border: "1px solid #cbd5e1", borderRadius: 6, padding: "4px 8px",
            fontSize: "0.9rem", width: 100, fontWeight: 600,
          }}
        />
        <span style={{ fontSize: "0.82rem", color: "#64748b" }}>of ${maxAmount.toFixed(2)} due</span>
      </div>
      <div className="card-element-wrapper">
        <CardElement options={CARD_OPTS} />
      </div>
      <button type="submit" disabled={loading} style={{ marginTop: "0.5rem" }}>
        {loading ? "Processing…" : `Pay $${parseFloat(customAmount || 0).toFixed(2)} AUD`}
      </button>
    </form>
  );
}

// ── My Welfare Contributions ────────────────────────────────
function MyWelfareContributions({ memberId }) {
  const [contributions, setContributions] = useState([]);
  const [pendingTotal,  setPendingTotal]  = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [payingId,      setPayingId]      = useState(null);

  const fetchContributions = useCallback(() => {
    setLoading(true);
    api.get("members/my-reimbursements/")
      .then(r => {
        setContributions(r.data.contributions || []);
        setPendingTotal(r.data.pending_total || 0);
      })
      .catch(e => {
        console.error(e);
        setError("Failed to load welfare contributions.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchContributions(); }, [fetchContributions]);

  const handlePaySuccess = () => {
    setPayingId(null);
    fetchContributions();
  };

  const statusBadge = (status) => {
    const map = {
      paid:    { label: "✅ Paid",    color: "#22c55e" },
      pending: { label: "⏳ Pending", color: "#f59e0b" },
      waived:  { label: "🟣 Waived",  color: "#8b5cf6" },
    };
    const s = map[status] || { label: status, color: "#6b7280" };
    return (
      <span style={{ color: s.color, fontWeight: 600, fontSize: "0.85rem" }}>
        {s.label}
      </span>
    );
  };

  if (loading) return <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>Loading contributions…</p>;
  if (error)   return <p style={{ color: "#ef4444", fontSize: "0.9rem" }}>{error}</p>;

  return (
    <div className="welfare-contributions-section" style={{ marginTop: "1.5rem" }}>
      <h4 style={{ margin: "0 0 0.75rem", color: "#1e293b" }}>
        📋 My Reimbursement Contributions
      </h4>

      {pendingTotal > 0 && (
        <div style={{
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderRadius: "8px",
          padding: "0.65rem 1rem",
          marginBottom: "0.9rem",
          fontSize: "0.9rem",
          color: "#92400e",
        }}>
          ⚠️ You have <strong>${pendingTotal.toFixed(2)} AUD</strong> in pending welfare reimbursement contributions.
        </div>
      )}

      {contributions.length === 0 ? (
        <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>
          No reimbursement contributions on record.
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ background: "#f1f5f9", textAlign: "left" }}>
                {["Beneficiary", "Reason", "Description", "Amount (AUD)", "Status", "Due Date", "Paid At", ""].map(h => (
                  <th key={h} style={{
                    padding: "0.5rem 0.75rem",
                    fontWeight: 600,
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contributions.map((c, i) => (
                <React.Fragment key={c.id}>
                  <tr style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                    <td style={{ padding: "0.5rem 0.75rem", color: "#1e293b" }}>
                      {c.beneficiary}
                    </td>
                    <td style={{ padding: "0.5rem 0.75rem", color: "#334155", textTransform: "capitalize" }}>
                      {c.reason}
                    </td>
                    <td style={{ padding: "0.5rem 0.75rem", color: "#475569", maxWidth: "200px" }}>
                      {c.description || "—"}
                    </td>
                    <td style={{ padding: "0.5rem 0.75rem", fontWeight: 600, color: "#1e293b" }}>
                      ${Number(c.amount).toFixed(2)}
                    </td>
                    <td style={{ padding: "0.5rem 0.75rem" }}>
                      {statusBadge(c.status)}
                    </td>
                    <td style={{ padding: "0.5rem 0.75rem", color: "#475569" }}>
                      {c.due_date || "—"}
                    </td>
                    <td style={{ padding: "0.5rem 0.75rem", color: "#475569" }}>
                      {c.paid_at ? c.paid_at.slice(0, 10) : "—"}
                    </td>
                    <td style={{ padding: "0.5rem 0.75rem" }}>
                      {c.status === "pending" && (
                        <button
                          onClick={() => setPayingId(payingId === c.id ? null : c.id)}
                          style={{
                            background: "#0070c0",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            padding: "0.3rem 0.75rem",
                            cursor: "pointer",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {payingId === c.id ? "✖ Cancel" : "💳 Pay Now"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {payingId === c.id && (
                    <tr>
                      <td colSpan={8} style={{ padding: "0.75rem 1rem", background: "#f0f7ff", borderBottom: "1px solid #e2e8f0" }}>
                        <Elements stripe={stripePromise}>
                          <ContributionPayForm
                            memberId={memberId}
                            contribution={c}
                            onSuccess={handlePaySuccess}
                          />
                        </Elements>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ──────────────────────────────────────────
export default function Dashboard() {
  const [data,                setData]                = useState(null);
  const [welfarePlans,        setWelfarePlans]        = useState([]);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [amountToPay,         setAmountToPay]         = useState(0);
  const [memberOpportunities, setMemberOpportunities] = useState([]);
  const [opportunityForm,     setOpportunityForm]     = useState({
    category: "job", title: "", description: "",
    location: "", salary: "", price: "", school: "", deadline: "",
  });
  const [editingOpportunity,    setEditingOpportunity]    = useState(null);
  const [isOpportunityModalOpen, setIsOpportunityModalOpen] = useState(false);
  const [isWelfareModalOpen,    setIsWelfareModalOpen]    = useState(false);

  const fetchMemberOpportunities = useCallback(async () => {
    try {
      const res = await api.get("members/opportunities/mine/");
      setMemberOpportunities(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get("members/dashboard/");
      setData(res.data);
      setSelectedInstallment(null);
      setAmountToPay(0);
      fetchMemberOpportunities();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) window.location.href = "/login";
    }
  }, [fetchMemberOpportunities]);

  const fetchWelfarePlans = async () => {
    try {
      const res = await api.get("members/welfare-plans/");
      setWelfarePlans(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    fetchDashboard();
    fetchWelfarePlans();
  }, [fetchDashboard]);

  if (!data) return <p>Loading...</p>;

  if (!data.membership_paid) {
    return (
      <div className="dashboard-container">
        <div className="payment-required-notice">
          <h2>⚠️ Membership Payment Required</h2>
          <p>Your membership payment is still pending. Please complete your payment to access the dashboard.</p>
          <p>If you've already paid, please allow some time for the payment to be processed.</p>
        </div>
      </div>
    );
  }

  const welfareData = data.welfare;
  const inWelfare   = data.in_welfare;

  let remainingOptions = [];
  if (inWelfare && welfareData && welfareData.remaining_installments > 0) {
    const { paid_installments, total_installments, remaining_installments } = welfareData;
    for (let i = 1; i <= remaining_installments; i++) {
      remainingOptions.push({
        label: `Installment ${paid_installments + i} / ${total_installments}`,
        value: i,
      });
    }
    if (remaining_installments > 1) {
      remainingOptions.push({ label: "Pay All Remaining", value: "all" });
    }
  }

  const handleWelfareInstallmentSelect = (e) => {
    const value = e.target.value;
    setSelectedInstallment(value);
    if (value === "all") {
      setAmountToPay(welfareData.welfare_remaining);
    } else {
      // For the last installment use remaining balance to avoid rounding issues
      const isLastInstallment =
        welfareData.paid_installments + 1 === welfareData.total_installments;
      setAmountToPay(
        isLastInstallment
          ? welfareData.welfare_remaining
          : welfareData.installment_amount
      );
    }
  };

  const handleOpportunityChange = (e) => {
    const { name, value } = e.target;
    setOpportunityForm({ ...opportunityForm, [name]: value });
  };

  const openOpportunityModal = (opportunity = null) => {
    if (opportunity) {
      setEditingOpportunity(opportunity);
      setOpportunityForm({
        category   : opportunity.category,
        title      : opportunity.title,
        description: opportunity.description,
        location   : opportunity.location   || "",
        salary     : opportunity.salary     || "",
        price      : opportunity.price      || "",
        school     : opportunity.school     || "",
        deadline   : opportunity.deadline   || "",
      });
    } else {
      setEditingOpportunity(null);
      setOpportunityForm({
        category: "job", title: "", description: "",
        location: "", salary: "", price: "", school: "", deadline: "",
      });
    }
    setIsOpportunityModalOpen(true);
  };

  const closeOpportunityModal = () => setIsOpportunityModalOpen(false);

  const handleOpportunitySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOpportunity) {
        await api.patch(`members/opportunities/${editingOpportunity.id}/`, opportunityForm);
      } else {
        await api.post("members/opportunities/", opportunityForm);
      }
      fetchMemberOpportunities();
      closeOpportunityModal();
    } catch { alert("Failed to save opportunity."); }
  };

  const handleDeleteOpportunity = async (op) => {
    if (!window.confirm("Are you sure you want to delete this opportunity?")) return;
    try {
      await api.delete(`members/opportunities/${op.id}/`);
      fetchMemberOpportunities();
    } catch { alert("Failed to delete opportunity."); }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>Welcome, {data.member_name}</h2>
        <p className="membership-status">
          <strong>Membership:</strong> {data.plan} - ✅ Paid (${data.plan_price.toFixed(2)})
        </p>
      </div>

      {/* Welfare Section */}
      <div className="welfare-section">
        <h3>Welfare Program</h3>
        {!inWelfare ? (
          <div className="welfare-not-enrolled">
            <p>You are not currently enrolled in the welfare program.</p>
            <button onClick={() => setIsWelfareModalOpen(true)} className="join-welfare-btn">
              Join Welfare Program
            </button>
          </div>
        ) : (
          <div className="welfare-enrolled">
            <div className="welfare-info">
              <p><strong>Plan:</strong> {welfareData.welfare_plan} ({welfareData.welfare_payment_plan})</p>
              <p><strong>Total Amount:</strong> ${welfareData.welfare_plan_price.toFixed(2)}</p>
              <p><strong>Total Paid:</strong> ${welfareData.welfare_total_paid.toFixed(2)}</p>
              <p><strong>Installments Paid:</strong> {welfareData.paid_installments} / {welfareData.total_installments}</p>
              <p><strong>Remaining Balance:</strong> ${welfareData.welfare_remaining.toFixed(2)}</p>
              <p><strong>Status:</strong> {welfareData.welfare_is_fully_paid ? "✅ Fully Paid" : "⏳ In Progress"}</p>
            </div>

            {welfareData.remaining_installments > 0 && (
              <div className="welfare-payment-section">
                <h4>Make a Payment</h4>
                <select onChange={handleWelfareInstallmentSelect} defaultValue="">
                  <option value="" disabled>Select installment to pay</option>
                  {remainingOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {selectedInstallment && (
                  <Elements stripe={stripePromise}>
                    <WelfarePaymentForm
                      memberId={data.member_id}
                      amount={amountToPay}
                      onPaymentSuccess={fetchDashboard}
                    />
                  </Elements>
                )}
              </div>
            )}

            {/* ── My Reimbursement Contributions ── */}
            <MyWelfareContributions memberId={data.member_id} />
          </div>
        )}
      </div>

      {/* Opportunities Section */}
      <div className="opportunities-section">
        <h3>Your Opportunities</h3>
        <button onClick={() => openOpportunityModal()} className="post-opportunity-btn">
          Post New Opportunity
        </button>
        <div className="opportunities-grid">
          {memberOpportunities.length === 0 ? (
            <p className="no-opportunities">You haven't posted any opportunities yet.</p>
          ) : (
            memberOpportunities.map((op) => (
              <div key={op.id} className="opportunity-card">
                <h4>{op.title}</h4>
                <p><b>Category:</b> {op.category}</p>
                <p>{op.description}</p>
                {op.location && <p><b>Location:</b> {op.location}</p>}
                {op.salary   && <p><b>Salary:</b>   {op.salary}</p>}
                {op.price    && <p><b>Price:</b>     {op.price}</p>}
                {op.school   && <p><b>School:</b>    {op.school}</p>}
                {op.deadline && <p><b>Deadline:</b>  {op.deadline}</p>}
                <div className="card-actions">
                  <button onClick={() => openOpportunityModal(op)}>Edit</button>
                  <button onClick={() => handleDeleteOpportunity(op)} className="delete-btn">
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Welfare Enrollment Modal */}
      <Modal isOpen={isWelfareModalOpen}
        onRequestClose={() => setIsWelfareModalOpen(false)}
        className="welfare-modal" overlayClassName="modal-overlay">
        <WelfareEnrollmentForm
          welfarePlans={welfarePlans}
          onClose={() => setIsWelfareModalOpen(false)}
          onSuccess={fetchDashboard}
        />
      </Modal>

      {/* Opportunity Modal */}
      <Modal isOpen={isOpportunityModalOpen}
        onRequestClose={closeOpportunityModal}
        className="opportunity-modal" overlayClassName="modal-overlay">
        <h3>{editingOpportunity ? "Edit Opportunity" : "Post Opportunity"}</h3>
        <form onSubmit={handleOpportunitySubmit} className="opportunity-form">
          <select name="category" value={opportunityForm.category}
            onChange={(e) => setOpportunityForm({ ...opportunityForm, category: e.target.value })}
            required>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {CATEGORY_FIELDS[opportunityForm.category].map((field) =>
            field.type === "textarea" ? (
              <textarea key={field.name} name={field.name} placeholder={field.label}
                value={opportunityForm[field.name] || ""}
                onChange={handleOpportunityChange} required={field.required} />
            ) : (
              <input key={field.name} type={field.type} name={field.name}
                placeholder={field.label} value={opportunityForm[field.name] || ""}
                onChange={handleOpportunityChange} required={field.required} />
            )
          )}
          <div className="form-actions">
            <button type="submit">{editingOpportunity ? "Update" : "Post"}</button>
            <button type="button" onClick={closeOpportunityModal} className="cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}